// Live in-call transcription client (Grok streaming STT via our Worker bridge).
//
// Flow: this opens a same-origin WebSocket to /api/crm/phone/stt-stream (the
// Worker injects XAI_API_KEY and proxies to wss://api.x.ai/v1/stt — the key
// never reaches the browser). We capture BOTH call legs with Web Audio, mix
// them to interleaved stereo PCM16 (ch0 = agent mic, ch1 = caller), and stream
// raw binary frames up. xAI returns transcript.partial / transcript.done JSON,
// which we surface line-by-line via onLine.
//
// Why this is safe for live calls: Web Audio only TAPS copies of the streams
// (MediaStreamSource), it never sits in the call's media path — so any STT
// failure is invisible to the call itself. Everything is wrapped; on error we
// tear down quietly and the call keeps running.
//
// Latency: interim_results=true + endpointing=10 (set on the bridge) + small
// ScriptProcessor frames + a pure pass-through bridge = words on screen within
// ~1s of speech. 16kHz mono-per-channel is the ASR sweet spot.

export interface TranscriptLine {
  channel: number      // 0 = you (agent mic), 1 = caller
  text: string
  isFinal: boolean     // false = interim (replace the live line); true = committed
}

interface XaiWord { text: string; start: number; end: number; speaker?: number }
interface XaiEvent {
  type: 'transcript.created' | 'transcript.partial' | 'transcript.done' | 'error'
  text?: string
  words?: XaiWord[]
  is_final?: boolean
  speech_final?: boolean
  channel_index?: number
  message?: string
}

const TARGET_SAMPLE_RATE = 16000
// 2048 frames ≈ 128ms at 16kHz — small enough for low latency, large enough to
// avoid flooding the socket. Drop to 1024 (~64ms) if live testing wants faster.
const FRAME_SIZE = 2048

export class LiveTranscriber {
  private ws: WebSocket | null = null
  private ctx: AudioContext | null = null
  private processor: ScriptProcessorNode | null = null
  private localSrc: MediaStreamAudioSourceNode | null = null
  private remoteSrc: MediaStreamAudioSourceNode | null = null
  private merger: ChannelMergerNode | null = null
  private sink: GainNode | null = null
  private localMic: MediaStream | null = null
  private ready = false
  private stopped = false

  private opts: {
    remoteStream: MediaStream                 // caller audio (from session.rtcPeerConnection receivers)
    onLine: (line: TranscriptLine) => void
    onError?: (msg: string) => void
  }

  constructor(opts: {
    remoteStream: MediaStream
    onLine: (line: TranscriptLine) => void
    onError?: (msg: string) => void
  }) {
    this.opts = opts
  }

  async start(): Promise<void> {
    try {
      // 1) Capture the local mic ourselves (a second getUserMedia on the same
      //    device is allowed; the SDK keeps its own for the call). Released on stop.
      this.localMic = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (this.stopped) return this.stop()

      // 2) Open the bridge socket. Same-origin wss → the CF_Authorization cookie
      //    rides the upgrade; the Worker auth-gates it.
      const scheme = location.protocol === 'https:' ? 'wss' : 'ws'
      console.info('[LiveTranscript] connecting to bridge…')
      this.ws = new WebSocket(`${scheme}://${location.host}/api/crm/phone/stt-stream`)
      this.ws.binaryType = 'arraybuffer'
      this.ws.onopen = () => console.info('[LiveTranscript] bridge open — waiting for xAI ready')
      this.ws.onmessage = (ev) => this.onMessage(ev)
      this.ws.onerror = () => this.opts.onError?.('Transcription connection error')
      this.ws.onclose = (ev) => { this.ready = false; console.info('[LiveTranscript] bridge closed', ev.code, ev.reason || '') }

      // 3) Web Audio graph: mic → merger ch0, caller → merger ch1 → processor →
      //    zero-gain sink → destination (sink at 0 prevents echo; the processor
      //    still needs a live destination connection to fire onaudioprocess).
      const Ctx = window.AudioContext || (window as any).webkitAudioContext
      this.ctx = new Ctx({ sampleRate: TARGET_SAMPLE_RATE })
      this.merger = this.ctx.createChannelMerger(2)
      this.localSrc = this.ctx.createMediaStreamSource(this.localMic)
      this.remoteSrc = this.ctx.createMediaStreamSource(this.opts.remoteStream)
      this.localSrc.connect(this.merger, 0, 0)
      this.remoteSrc.connect(this.merger, 0, 1)
      this.processor = this.ctx.createScriptProcessor(FRAME_SIZE, 2, 2)
      this.processor.onaudioprocess = (e) => this.onAudio(e)
      this.sink = this.ctx.createGain()
      this.sink.gain.value = 0
      this.merger.connect(this.processor)
      this.processor.connect(this.sink)
      this.sink.connect(this.ctx.destination)
    } catch (e: any) {
      this.opts.onError?.(e?.message || 'Could not start transcription')
      this.stop()
    }
  }

  private onAudio(e: AudioProcessingEvent): void {
    if (!this.ready || this.stopped || this.ws?.readyState !== WebSocket.OPEN) return
    const ch0 = e.inputBuffer.getChannelData(0)
    const ch1 = e.inputBuffer.getChannelData(1)
    const n = ch0.length
    // Interleave to stereo PCM16 little-endian: [L0,R0,L1,R1,...]
    const pcm = new Int16Array(n * 2)
    for (let i = 0; i < n; i++) {
      pcm[i * 2] = Math.max(-32768, Math.min(32767, ch0[i] * 32767))
      pcm[i * 2 + 1] = Math.max(-32768, Math.min(32767, ch1[i] * 32767))
    }
    try { this.ws.send(pcm.buffer) } catch { /* socket closing */ }
  }

  private onMessage(ev: MessageEvent): void {
    let msg: XaiEvent
    try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : new TextDecoder().decode(ev.data)) } catch { return }
    switch (msg.type) {
      case 'transcript.created':
        this.ready = true // only now may we send audio
        console.info('[LiveTranscript] xAI ready — streaming audio')
        break
      case 'transcript.partial': {
        // interim_results=true → each segment streams as interim updates
        // (is_final:false), then one finalized result (is_final:true). Every
        // committed segment arrives HERE, so this is the single source of lines.
        const text = (msg.text || '').trim()
        if (!text) break
        this.opts.onLine({
          channel: msg.channel_index ?? 0,
          text,
          isFinal: !!msg.is_final,
        })
        break
      }
      case 'transcript.done':
        // Terminal end-of-stream marker only. We deliberately do NOT emit a line:
        // its text would duplicate the last segment already sent via a partial
        // (or, if it carries the whole transcript, dump it as one giant line).
        console.info('[LiveTranscript] transcript.done')
        break
      case 'error':
        this.opts.onError?.(msg.message || 'Transcription error')
        break
    }
  }

  stop(): void {
    this.stopped = true
    this.ready = false
    try { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'audio.done' })) } catch { /* */ }
    try { this.processor?.disconnect(); if (this.processor) this.processor.onaudioprocess = null } catch { /* */ }
    try { this.localSrc?.disconnect() } catch { /* */ }
    try { this.remoteSrc?.disconnect() } catch { /* */ }
    try { this.merger?.disconnect() } catch { /* */ }
    try { this.sink?.disconnect() } catch { /* */ }
    try { this.localMic?.getTracks().forEach(t => t.stop()) } catch { /* */ }
    try { this.ctx?.close() } catch { /* */ }
    try { this.ws?.close() } catch { /* */ }
    this.ws = null; this.ctx = null; this.processor = null
    this.localSrc = null; this.remoteSrc = null; this.merger = null; this.sink = null; this.localMic = null
  }
}
